import axios from "axios";
import assetProHttp from "./Api";

/**
 * Client for the Ops module (iot-ops) integration API.
 *
 * Deliberately a SEPARATE axios instance from util/Api: that instance's
 * interceptor attaches the user's AssetPro JWT to every request, which must
 * never be sent to another service. Here the only credential is the
 * organisation's Ops integration token, sent per call in X-INTEGRATION-KEY.
 *
 * Every AssetPro environment (production, staging, demo) reads tickets from
 * production Ops. REACT_APP_OPS_API_URL exists only to point a local build at
 * a local Ops backend while developing.
 *
 * Contract: iot-ops/docs/assetpro-support-tickets-api.md
 */
export const OPS_API_BASE_URL = process.env.REACT_APP_OPS_API_URL || "https://opsapi.theassetpro.com";

const opsHttp = axios.create({ baseURL: OPS_API_BASE_URL, timeout: 30000 });

const keyHeader = (token) => ({ headers: { "X-INTEGRATION-KEY": token } });

/**
 * The organisation's Ops integration token from AssetPro Settings
 * (GET /setting/organisation/:id -> accessToken), or an error message saying
 * why it can't be used. Only iotint_ tokens are ever sent to Ops.
 */
export const readOpsToken = async (orgId) => {
  if (!orgId) {
    return { error: "Organisation of the logged-in user is not known, so the Ops token cannot be read." };
  }
  let response;
  try {
    response = await assetProHttp.get(`/setting/organisation/${orgId}`);
  } catch (error) {
    const status = error && error.response && error.response.status;
    if (status === 401 || status === 403) {
      return { error: "You need access to Settings to view Ops tickets (the Ops token is stored there)." };
    }
    return { error: `Could not load organisation settings${status ? ` (HTTP ${status})` : ""}.` };
  }
  const token = response && response.data && response.data.accessToken;
  // The Settings form stores an unset value as the string 'null'.
  if (!token || token === "null") {
    return { error: "Ops integration token is not configured. Add it under Settings → Access Token." };
  }
  if (!token.startsWith("iotint_")) {
    return { error: 'The Access Token in Settings is not an Ops integration token (expected it to start with "iotint_").' };
  }
  return { token };
};

/**
 * The token organisation's tickets (Ops scopes by token), newest update first.
 * `state` is 'open' (status category not CLOSED) or 'closed' (CLOSED).
 */
export const listOpsTickets = (token, state) =>
  opsHttp.get("/integration/support-tickets", { ...keyHeader(token), params: { state } });

/**
 * Tickets whose stored assetCode is exactly `assetCode` (Ops does the match).
 * Optional `state`: 'open' | 'closed'.
 */
export const listOpsTicketsForAsset = (token, assetCode, state) =>
  opsHttp.get("/integration/support-tickets", { ...keyHeader(token), params: { assetCode, state } });

/** Active troubleshooting-guide issue types: [{ code, category, issue, symptoms }]. */
export const listOpsComplaintTypes = (token) =>
  opsHttp.get("/integration/support-tickets/complaint-types", keyHeader(token));

/**
 * Raises a ticket in Ops (tickets:write). For a ticket raised from the asset
 * report no job card is sent, so assetCode + detailsOfComplaint are required.
 * Returns { created, ticket }.
 */
export const createOpsTicket = (token, body) =>
  opsHttp.post("/integration/support-tickets", body, keyHeader(token));

/** Uploads one file to a ticket (multipart, field "file"; Ops limit 15 MB). */
export const uploadOpsAttachment = (token, ticketId, file, { caption, uploaderName } = {}) => {
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("caption", caption);
  if (uploaderName) form.append("uploaderName", uploaderName);
  return opsHttp.post(`/integration/support-tickets/${encodeURIComponent(ticketId)}/attachments`, form, {
    ...keyHeader(token),
    timeout: 120000,
  });
};

export const OPS_MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

/** One ticket with its full updates[] timeline. */
export const getOpsTicket = (token, ticketId) =>
  opsHttp.get(`/integration/support-tickets/${encodeURIComponent(ticketId)}`, keyHeader(token));

/**
 * One attachment's bytes as a Blob. Ops serves files only through this
 * authenticated route (no public URL), so the browser cannot just link to it.
 */
export const downloadOpsAttachment = (token, attachmentId) =>
  opsHttp.get(`/integration/support-tickets/attachments/${encodeURIComponent(attachmentId)}/download`, {
    ...keyHeader(token),
    responseType: "blob",
  });

/**
 * Turns an Ops call failure into a message that says what actually went wrong.
 * A failed call is never reported as "no tickets".
 */
export const describeOpsError = (error) => {
  const response = error && error.response;
  if (!response) {
    return "Could not reach the Ops module (network or CORS failure). Tickets could not be loaded.";
  }
  const serverMessage = response.data && response.data.message;
  switch (response.status) {
    case 400:
      return `Ops rejected the request: ${serverMessage || "bad request"}`;
    case 401:
      return "Ops integration token is invalid, revoked or expired. Update it in Settings.";
    case 403:
      return "Ops integration token does not have the tickets:read scope, or the ticket belongs to another organisation.";
    case 404:
      return "Ticket not found in the Ops module.";
    case 429:
      return "Too many requests to the Ops module. Wait a minute and try again.";
    default:
      return `Ops module error (HTTP ${response.status})${serverMessage ? `: ${serverMessage}` : ""}`;
  }
};
